// ============================================================
//  nearme. — Supabase API Layer
//  src/lib/supabase.js
// ============================================================

import { createClient } from '@supabase/supabase-js';

export const supabase = createClient(
  'https://kytmupbvcidcuqhxpupb.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt5dG11cGJ2Y2lkY3VxaHhwdXBiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzkzNzQ3ODAsImV4cCI6MjA5NDk1MDc4MH0.2FE1OBRbbQk_qUS68DXdiiUSHzbglcojtWs97NbmftY'
);


// ──────────────────────────────────────────
// AUTH
// ──────────────────────────────────────────

export async function signUp({ email, password, name }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name } },
  });
  if (error) throw error;
  return data;
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export async function getMyProfile() {
  const user = await getCurrentUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .single();
  if (error) throw error;
  return data;
}

export function onAuthChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session?.user ?? null);
  });
}


// ──────────────────────────────────────────
// USER PROFILE
// ──────────────────────────────────────────

export async function updateProfile({ name, handle, bio, interests, avatar_url, location_text, show_location }) {
  const user = await getCurrentUser();
  const updates = { name, handle, bio, interests, avatar_url };
  if (location_text !== undefined) updates.location_text = location_text;
  if (show_location !== undefined) updates.show_location = show_location;
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', user.id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}


// ──────────────────────────────────────────
// SELLERS
// ──────────────────────────────────────────

export async function createSellerProfile({ trade, parent_cat, seller_tag, dorm, lat, lng }) {
  const user = await getCurrentUser();
  await supabase.from('users').update({ role: 'seller' }).eq('id', user.id);
  const { data, error } = await supabase
    .from('sellers')
    .insert({
      user_id: user.id,
      trade,
      parent_cat,
      seller_tag,
      dorm,
      location: lat && lng ? `POINT(${lng} ${lat})` : null,
    })
    .select()
    .single();
  if (error) throw error;
  if (data?.id) return data;
  // SELECT after INSERT may be blocked by RLS — fetch the row directly
  const { data: fetched } = await supabase
    .from('sellers')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();
  if (fetched?.id) return fetched;
  throw new Error("Seller profile was created but could not be retrieved. Please try again.");
}

export async function getSellerByUserId(userId) {
  const { data, error } = await supabase
    .from('seller_cards')
    .eq('user_id', userId)
    .single();
  if (error) throw error;
  return data;
}

export async function getSellerById(sellerId) {
  const { data, error } = await supabase
    .from('seller_cards')
    .eq('seller_id', sellerId)
    .single();
  if (error) throw error;
  return data;
}

export async function getSellers({ trade, parentCat, sort = 'rating', limit = 40 }) {
  let query = supabase
    .from('seller_cards')
    .select('*, listings(id, name, price, kind, slot_minutes, is_flash, image_urls)')
    .limit(limit);

  if (trade)     query = query.eq('trade', trade);
  if (parentCat) query = query.eq('parent_cat', parentCat);
  if (sort === 'rating')    query = query.order('rating', { ascending: false });
  if (sort === 'followers') query = query.order('follower_count', { ascending: false });
  if (sort === 'newest')    query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getSellersNearby({ lat, lng, radiusMeters = 2000 }) {
  const { data, error } = await supabase.rpc('sellers_within_radius', {
    lat,
    lng,
    radius_meters: radiusMeters,
  });
  if (error) throw error;
  return data;
}

export async function setFlashActive(sellerId, active, minutesFrom = 120) {
  const flash_until = active
    ? new Date(Date.now() + minutesFrom * 60 * 1000).toISOString()
    : null;
  const { error } = await supabase
    .from('sellers')
    .update({ flash_active: active, flash_until })
    .eq('id', sellerId);
  if (error) throw error;
}


// ──────────────────────────────────────────
// LISTINGS
// ──────────────────────────────────────────

export async function createListing({ seller_id, name, description, price, kind, category, image_urls, slot_minutes, is_active, is_flash, message_to_book }) {
  const { data, error } = await supabase
    .from('listings')
    .insert({ seller_id, name, description, price, kind, category, image_urls, slot_minutes, is_active, is_flash, message_to_book: message_to_book || false })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getListings({ category, kind, flashOnly, sortBy, limit = 40 }) {
  let query = supabase
    .from('listings')
    .select(`
      *,
      sellers (
        id, dorm, rating, review_count, retention_pct, flash_active, location,
        users ( handle, name, avatar_url )
      )
    `)
    .eq('is_active', true)
    .limit(limit);

  if (category)  query = query.eq('category', category);
  if (kind)      query = query.eq('kind', kind);
  if (flashOnly) query = query.eq('is_flash', true);
  if (sortBy === 'quickest')   query = query.order('slot_minutes', { ascending: true });
  if (sortBy === 'price_asc')  query = query.order('price', { ascending: true });
  if (sortBy === 'top_rated')  query = query.order('sellers(rating)', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getFlashFeed() {
  const { data, error } = await supabase
    .from('flash_feed')
    .select('*')
    .order('slot_minutes', { ascending: true });
  if (error) throw error;
  return data;
}

export async function setListingFlash(listingId, active, minutesUntil = 120) {
  const flash_until = active
    ? new Date(Date.now() + minutesUntil * 60 * 1000).toISOString()
    : null;
  const { error } = await supabase
    .from('listings')
    .update({ is_flash: active, flash_until })
    .eq('id', listingId);
  if (error) throw error;
}

export async function toggleSave(listingId) {
  const user = await getCurrentUser();
  const { data: existing } = await supabase
    .from('saves')
    .select('listing_id')
    .eq('user_id', user.id)
    .eq('listing_id', listingId)
    .single();

  if (existing) {
    await supabase.from('saves').delete().eq('user_id', user.id).eq('listing_id', listingId);
    return false;
  } else {
    await supabase.from('saves').insert({ user_id: user.id, listing_id: listingId });
    return true;
  }
}

export async function getMySaves() {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('saves')
    .select('listing_id, listings(*, sellers(id, dorm, rating, users(handle, name, avatar_url)))')
    .eq('user_id', user.id);
  if (error) throw error;
  return data.map(s => s.listings);
}


// ──────────────────────────────────────────
// FEED / POSTS
// ──────────────────────────────────────────

export async function getFollowingFeed(limit = 30) {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('following_feed')
    .select('*')
    .eq('follower_id', user.id)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function getForYouFeed(interests = [], limit = 30) {
  const { data, error } = await supabase
    .from('posts')
    .select(`
      *,
      sellers (
        id, trade, dorm, rating,
        users ( handle, name, avatar_url )
      )
    `)
    .in('sellers.trade', interests)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function createPost({ seller_id, kind, caption, image_urls, price, listing_id, is_flash }) {
  const { data, error } = await supabase
    .from('posts')
    .insert({ seller_id, kind, caption, image_urls, price, listing_id, is_flash })
    .select()
    .single();
  if (error) throw error;
  return data;
}


// ──────────────────────────────────────────
// FOLLOWS
// ──────────────────────────────────────────

export async function toggleFollow(sellerId) {
  const user = await getCurrentUser();
  const { data: existing } = await supabase
    .from('follows')
    .select('seller_id')
    .eq('follower_id', user.id)
    .eq('seller_id', sellerId)
    .single();

  if (existing) {
    await supabase.from('follows').delete().eq('follower_id', user.id).eq('seller_id', sellerId);
    return false;
  } else {
    await supabase.from('follows').insert({ follower_id: user.id, seller_id: sellerId });
    return true;
  }
}

export async function getMyFollowing() {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('follows')
    .select('seller_id, sellers(*, users(handle, name, avatar_url))')
    .eq('follower_id', user.id);
  if (error) throw error;
  return data.map(f => f.sellers);
}

export async function isFollowing(sellerId) {
  const user = await getCurrentUser();
  const { data } = await supabase
    .from('follows')
    .select('seller_id')
    .eq('follower_id', user.id)
    .eq('seller_id', sellerId)
    .single();
  return !!data;
}


// ──────────────────────────────────────────
// BOOKINGS
// ──────────────────────────────────────────

export async function createBooking({ listing_id, seller_id, amount_cents, scheduled_for, notes }) {
  const user = await getCurrentUser();
  const platform_fee_cents  = Math.round(amount_cents * 0.10);
  const stripe_fee_cents    = Math.round(amount_cents * 0.029) + 30;
  const seller_payout_cents = amount_cents - platform_fee_cents - stripe_fee_cents;

  const { data, error } = await supabase
    .from('bookings')
    .insert({
      listing_id,
      buyer_id: user.id,
      seller_id,
      status: 'pending',
      amount_cents,
      platform_fee_cents,
      stripe_fee_cents,
      seller_payout_cents,
      scheduled_for,
      notes,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getMyBookings() {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      listings ( name, image_urls, kind ),
      sellers ( id, users ( handle, name, avatar_url ) )
    `)
    .eq('buyer_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getSellerBookings(sellerId) {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      listings ( name, kind ),
      users!buyer_id ( handle, name, avatar_url )
    `)
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function confirmBooking(bookingId) {
  const { error } = await supabase
    .from('bookings')
    .update({ status: 'confirmed' })
    .eq('id', bookingId);
  if (error) throw error;
}

export async function completeBooking(bookingId) {
  const { error } = await supabase
    .from('bookings')
    .update({ status: 'completed' })
    .eq('id', bookingId);
  if (error) throw error;
}


// ──────────────────────────────────────────
// REVIEWS
// ──────────────────────────────────────────

export async function leaveReview({ booking_id, seller_id, rating, body }) {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('reviews')
    .insert({ booking_id, buyer_id: user.id, seller_id, rating, body })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getSellerReviews(sellerId) {
  const { data, error } = await supabase
    .from('reviews')
    .select('*, users!buyer_id(handle, name, avatar_url)')
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}


// ──────────────────────────────────────────
// MESSAGES
// ──────────────────────────────────────────

export async function sendMessage({ receiver_id, body, booking_id }) {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('messages')
    .insert({ sender_id: user.id, receiver_id, body, booking_id })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getConversation(otherUserId) {
  const user = await getCurrentUser();
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
    .or(`sender_id.eq.${otherUserId},receiver_id.eq.${otherUserId}`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export function subscribeToMessages(userId, onMessage) {
  return supabase
    .channel('messages')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `receiver_id=eq.${userId}`,
    }, payload => onMessage(payload.new))
    .subscribe();
}


// ──────────────────────────────────────────
// REAL-TIME SUBSCRIPTIONS
// ──────────────────────────────────────────

export function subscribeToFlash(onFlash) {
  return supabase
    .channel('flash')
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'listings',
      filter: 'is_flash=eq.true',
    }, payload => onFlash(payload))
    .subscribe();
}

export function subscribeToBooking(bookingId, onUpdate) {
  return supabase
    .channel(`booking-${bookingId}`)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'bookings',
      filter: `id=eq.${bookingId}`,
    }, payload => onUpdate(payload.new))
    .subscribe();
}

export function unsubscribe(channel) {
  supabase.removeChannel(channel);
}


// ──────────────────────────────────────────
// FILE UPLOAD
// ──────────────────────────────────────────

export async function uploadPhoto(file, bucket = 'listings') {
  const user = await getCurrentUser();
  const ext  = file.name.split('.').pop();
  const path = `${user.id}/${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: true });
  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}


// ──────────────────────────────────────────
// WAITLIST
// ──────────────────────────────────────────

export async function joinWaitlist({ name, email, phone, role, trade, dorm }) {
  const { error } = await supabase
    .from('waitlist')
    .insert({ name, email, phone, role, trade, dorm });
  if (error) throw error;
}